import {
    For,
    Show,
    lazy,
} from 'solid-js'
import { useIntl } from '@cookbook/solid-intl'

import ButtonAtomComponent from '../../atoms/button-atom/button-atom-component.jsx'
import ImageAtomComponent from '../../atoms/image-atom/image-atom-component.jsx'
import MenuMoleculeComponent from '../../molecules/menu-molecule/menu-molecule-component.jsx'
import StaticSVG from '../../atoms/static-svg.jsx'
import { buildLocaleHref } from '../../../utils/locale-href.js'
import { platform } from '../../../../../.configs/app/environment.js'
// The template's own catalog, not a copy: duplicating the strings drifted on the first attempt
// ("Become a host" vs "Become a Host"), and these ids already carry translations.
import { messages } from '../../templates/public-template/public-template-component.messages.en.js'
// The locale control's accessible name. Borrowed from the modal that control opens in the SPA
// rather than adding a fourth translation of "choose a language" to three catalogs.
import { messages as localeSelectMessages } from '../../../modals/locale-select-modal/locale-select-modal.messages.en.js'

// TENANT-0000. The app's back control reads the router, which the website's
// prerender must never reach (prerender-import-guard follows static imports
// only). Loaded on demand, it stays out of that bundle and the website's; it is
// only ever rendered in the app.
// eslint-disable-next-line node/no-unsupported-features/es-syntax -- a bundler code-split point, not Node
const LodgrAppBackButton = lazy(() => import('../../lodgr/lodgr-app-back-button/lodgr-app-back-button.jsx'))

/**
 * Destinations that leave the menu bar below the 1184px break (styled in
 * public-template-component.scss), so the translated nav fits from 768px up. They stay in the
 * phone-width disclosure. [TENANT-5699]
 */
const COLLAPSIBLE_DESTINATION_CLASS = 'public-template-component__header__button public-template-component__header__button--collapsible'

/**
 * SiteHeader - the guest-facing header: logo, primary destinations, sign in.
 *
 * Extracted from `public-template-component.jsx` so it can render in two places that need it to
 * behave differently:
 *
 * - In the SPA the template passes click handlers, and each destination renders as a button that
 *   navigates through the router and fires its analytics event. Behaviour is unchanged.
 * - In a prerendered landing page there is no router, no session and no JavaScript, so the same
 *   markup renders as plain anchors. That is the whole point: a header of buttons is inert in a
 *   static page, and the visitor would have no way out of it.
 *
 * The class names are deliberately the template's own (`public-template-component__header__*`), so
 * the existing stylesheet applies to both without a second set of rules to keep in step.
 *
 * @param {object} props
 * @param {string} props.locale - Active locale; hrefs are built against it rather than the URL,
 *   which does not exist during a prerender.
 * @param {string} props.logoSrc
 * @param {boolean} [props.isAuthenticated]
 * @param {() => void} [props.onSearchClick] - When absent, that destination renders as an anchor.
 * @param {() => void} [props.onNewsroomClick]
 * @param {() => void} [props.onExtensionClick]
 * @param {() => void} [props.onHostClick]
 * @param {() => void} [props.onSignInClick] - Click on the last destination, "Login". The template
 *   decides what it does: the auth modal signed out, the dashboard signed in. The label is "Login"
 *   in both states; TENANT-5814 briefly renamed it "Account" → /account while signed in, which
 *   broke the login journey (sign in, return to the homepage, land on /account), so it was put back.
 * @param {boolean} [props.showMenu] - The template hides the menu on some routes; defaults to shown,
 *   which is what a landing page wants.
 * @param {string} [props.navMenuLabel] - Accessible name for the phone-width menu disclosure, and
 *   the switch that renders it at all: supplied by the prerendered pages, withheld by the SPA
 *   template, which draws its own hamburger.
 * @param {Array<{ href: string, label: string, locale: string }>} [props.localeLinks] - Renders the
 *   header's locale control as a no-JavaScript disclosure over these links. Passed ONLY by the
 *   prerender entry: on every SPA route the template already renders its own locale button beside
 *   this header, and a second one would double it on the guest homepage.
 *   [doc: run log, Phase 8c, item 5]
 */
export default function SiteHeader (props) {
    const intl = useIntl()

    const hrefFor = (path) => buildLocaleHref(path, props.locale)

    /**
     * One destination. A handler makes it a button (SPA); no handler makes it a link (prerender).
     */
    const Destination = (destinationProps) => (
        <Show
            when={destinationProps.onClick}
            fallback={
                // `button-atom-component` is carried by hand here. Every header-button rule in the
                // stylesheet is written as `.menu-molecule-component .button-atom-component.
                // public-template-component__header__button`, so an anchor without that class
                // matches NONE of them: the prerendered pages shipped a header of UA-default blue
                // underlined links, wide enough to push the document's scrollWidth 48px past the
                // viewport. The SPA never saw it because its destinations all take an onClick and
                // go through `ButtonAtomComponent`, which adds the class itself.
                // The variant class is threaded through the same way, so the "Listing Search"
                // destination keeps its pill on both paths. [doc: run log, Phase 8b]
                <a
                    class={`button-atom-component ${destinationProps.class ?? 'public-template-component__header__button'}`}
                    href={hrefFor(destinationProps.href)}
                >
                    {destinationProps.label}
                </a>
            }
        >
            <ButtonAtomComponent
                class={destinationProps.class ?? 'public-template-component__header__button'}
                onClick={destinationProps.onClick}
            >
                {destinationProps.label}
            </ButtonAtomComponent>
        </Show>
    )

    /**
     * The header's destinations in frame order, as data. The disclosure below renders the same set
     * as the menu above it, and a second hand-written copy is how a link added to one path goes
     * missing from the other. [TENANT-5318 narrow-width fix 2]
     */
    /*
        TENANT-5315: the mobile app has no homepage, newsroom, extension or
        become-a-host pages (the extension cannot exist on a phone), so the logo
        returns to the app's landing surface and the web-only destinations are
        filtered out of this ONE list rather than hidden in a second copy.
        TENANT-5565: the reverse for Lodgr Insights — an app-only page that the
        website never routes, filtered the same way (`appOnly`).
    */
    const destinations = () => [
        {
            class: 'public-template-component__header__button public-template-component__header__button--search',
            href: '/search',
            label: intl.formatMessage(messages.propertySearchButton),
            onClick: props.onSearchClick,
        },
        {
            appOnly: true,
            href: '/insights',
            label: intl.formatMessage(messages.lodgrInsightsButton),
        },
        {
            class: COLLAPSIBLE_DESTINATION_CLASS,
            href: '/newsroom',
            label: intl.formatMessage(messages.newsroomButton),
            onClick: props.onNewsroomClick,
            webOnly: true,
        },
        {
            class: COLLAPSIBLE_DESTINATION_CLASS,
            href: '/browser-extension',
            label: intl.formatMessage(messages.browserExtensionButton),
            onClick: props.onExtensionClick,
            webOnly: true,
        },
        {
            href: '/become-a-host',
            label: intl.formatMessage(messages.becomeAHostButton),
            onClick: props.onHostClick,
            webOnly: true,
        },
        // Always "Login": the SPA handler decides where a click goes (auth modal signed out,
        // dashboard signed in); the prerender link goes to /account or /login.
        {
            href: props.isAuthenticated ? '/account' : '/login',
            label: intl.formatMessage(messages.loginButton),
            onClick: props.onSignInClick,
        },
    ].filter((destination) => !(platform.isMobileApp && destination.webOnly) && !(platform.isWeb && destination.appOnly))

    return (
        <>
            {/* TENANT-0000. The app's global back control; the website has the browser's. */}
            <Show when={platform.isMobileApp}>
                <LodgrAppBackButton />
            </Show>
            <a class="public-template-component__header__link" href={hrefFor(platform.isMobileApp ? '/search' : '/')}>
                <ImageAtomComponent
                    class="public-template-component__header__link__logo"
                    src={props.logoSrc}
                    alt="Lodgr"
                />
            </a>
            {/* Named, not `alt=""`. The two logo links look like duplicates that ought to leave one
                of them decorative, and they are not: `public-template-component.scss` makes them a
                breakpoint pair, so exactly one is ever on screen. `&__link--mobile` is `display: none`
                until 767px and `&__link__logo` — the image in the anchor above — is `display: none`
                from 767px down. A screen reader therefore never meets both, and an empty alt here
                would leave the home link with no accessible name at every width this anchor is the
                visible one, which is the state it shipped in: no alt attribute at all, an anchor whose
                only content is that image, so the only thing left to announce was the URL. */}
            <a class="public-template-component__header__link--mobile" href={hrefFor(platform.isMobileApp ? '/search' : '/')}>
                <ImageAtomComponent
                    alt="Lodgr"
                    class="public-template-component__header__link--mobile__logo"
                    src={props.logoSrc}
                />
            </a>

            <Show when={props.showMenu ?? true}>
                <MenuMoleculeComponent class="public-template-component__header__menu">
                    <For each={destinations()}>
                        {(destination) => (
                            <Destination
                                class={destination.class}
                                href={destination.href}
                                label={destination.label}
                                onClick={destination.onClick}
                            />
                        )}
                    </For>
                </MenuMoleculeComponent>
            </Show>

            {/* The phone-width menu. Below 768 the destinations above are `display: none` and the
                hamburger that replaces them belongs to the SPA's template, not to this header — so
                a prerendered page had no navigation at all under that width. This is a `<details>`
                for the same reason the locale control below is one: the island mounts the search
                card and the map and nothing else, so a `<button>` here would be a control whose
                handler never attaches.

                Gated on `navMenuLabel` rather than on a boolean, so the prop that supplies the
                accessible name is the same one that turns the control on — the SPA renders this
                header inside the template that already draws a hamburger, and passing neither is
                what keeps it from drawing two. Same shape as `localeLinks` below.
                [TENANT-5318 narrow-width fix 2] */}
            <Show when={props.navMenuLabel && (props.showMenu ?? true)}>
                <details class="site-header__nav">
                    <summary
                        aria-label={props.navMenuLabel}
                        class="public-template-component__header__hamburger site-header__nav-summary"
                    >
                        {/* The template's own icon files, so the control is the one the rest of the
                            site draws. Which of the two shows is `[open]` in CSS, because nothing
                            here re-renders on a toggle. */}
                        <ImageAtomComponent
                            alt=""
                            class="site-header__nav-icon site-header__nav-icon--closed"
                            src="/assets/icons/hamburger-outlined.svg"
                        />
                        <ImageAtomComponent
                            alt=""
                            class="site-header__nav-icon site-header__nav-icon--open"
                            src="/assets/icons/close-filled.svg"
                        />
                    </summary>
                    <ul class="site-header__nav-menu">
                        <For each={destinations()}>
                            {(destination) => (
                                <li>
                                    <a class="site-header__nav-link" href={hrefFor(destination.href)}>
                                        {destination.label}
                                    </a>
                                </li>
                            )}
                        </For>
                    </ul>
                </details>
            </Show>

            {/* The frame's locale button. Same class, same icon and therefore the same round grey
                pill the SPA's template renders — but a `<details>` rather than a `<button>`, because
                a prerendered page has no JavaScript to open the locale modal with, and the locale's
                own page is a link the build already knows. */}
            <Show when={props.localeLinks?.length > 1}>
                <details class="site-header__locale">
                    <summary
                        class="public-template-component__checkout-header__locale-selector site-header__locale-summary"
                        aria-label={intl.formatMessage(localeSelectMessages.languageSection)}
                    >
                        <StaticSVG
                            class="public-template-component__checkout-header__locale-selector__icon"
                            name="earth-americas-outlined"
                        />
                    </summary>
                    <ul class="site-header__locale-menu">
                        <For each={props.localeLinks}>
                            {(localeLink) => (
                                <li>
                                    <a
                                        class="site-header__locale-link"
                                        href={localeLink.href}
                                        hreflang={localeLink.locale}
                                        lang={localeLink.locale}
                                    >
                                        {localeLink.label}
                                    </a>
                                </li>
                            )}
                        </For>
                    </ul>
                </details>
            </Show>
        </>
    )
}
